/**
 * Typed wrapper around fetch for the demo-storefront API.
 *
 * Session is cookie-based (`vd_session` httpOnly cookie). Browser sends it
 * automatically on same-origin or with credentials:'include' on cross-origin.
 *
 * In dev Vite proxies /api/* to the storefront. In docker the nginx config
 * does the same. Set `VITE_STOREFRONT_API` to override the base.
 */

import type {
  AuthMagicLinkRequestResponse,
  AuthMagicLinkVerifyResponse,
  AuthSmsRequestResponse,
  AuthSmsVerifyResponse,
  AuthMeResponse,
  Cart,
  Category,
  LoyaltyPromoCode,
  LoyaltyTransaction,
  LoyaltyView,
  NboOffer,
  Order,
  Outlet,
  PlaceOrderInput,
  Product,
  Profile,
  PromoBanner,
  PromoPreview,
} from "./types";

const DEFAULT_BASE = "/api";

function getBase(): string {
  // §5.11 Ф4 — рантайм-инъекция базы API (для развёртывания витрины под путём,
  // напр. /kafe/api на общем домене). Приоритетнее build-time env.
  if (typeof window !== "undefined") {
    const inj = (window as unknown as { __API_BASE__?: string }).__API_BASE__;
    if (inj) return inj;
  }
  // If env var is empty/undefined we use /api which Vite proxies in dev.
  const envBase =
    typeof import.meta !== "undefined" && import.meta.env?.VITE_STOREFRONT_API;
  if (!envBase) return DEFAULT_BASE;
  // env var points at storefront origin → suffix /api ourselves.
  const trimmed = envBase.replace(/\/$/, "");
  // If user already included /api, leave as-is.
  if (trimmed.endsWith("/api")) return trimmed;
  return `${trimmed}/api`;
}

/** Ограничения покупателя (дорожка Д): варианты и подписи — из словаря магазина. */
export interface CustomerConstraintOption { value: string; label: string; short_label?: string; group?: string; kind: "forbidden" | "requirement" }
export interface CustomerConstraints {
  registry: CustomerConstraintOption[]; forbidden: string[]; requirements: string[]; version: string;
  hidden_by_requirements: number; consent: { id: string; version: string; text: string } | null;
}

export class StorefrontApiError extends Error {
  statusCode: number;
  payload: unknown;
  constructor(message: string, statusCode: number, payload: unknown) {
    super(message);
    this.statusCode = statusCode;
    this.payload = payload;
  }
}

type FetchFn = typeof fetch;

export interface ClientOptions {
  base?: string;
  fetchImpl?: FetchFn;
}

// VD — часовой пояс браузера (IANA), считаем один раз. Уходит в заголовке
// X-Timezone на каждом запросе → demo-storefront кладёт в payload.timezone
// событий → ядро пишет в profile.timezone (тихие часы в ЧП абонента).
const DEVICE_TZ = ((): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
})();

export class StorefrontClient {
  base: string;
  private fetchImpl: FetchFn;

  constructor(opts: ClientOptions = {}) {
    this.base = opts.base ?? getBase();
    this.fetchImpl =
      opts.fetchImpl ??
      ((input, init) => globalThis.fetch(input, init));
  }

  /**
   * Первый запрос страницы заводит гостю анонимную сессию (cookie). Параллельные первые запросы до получения cookie
   * заводили КАЖДЫЙ свою сессию и свой профиль: шапка читала корзину одного гостя, помощник собирал в корзину другого
   * (приёмка 02.10: панель «3 товара», шапка «0 ₽»). Поэтому остальные запросы ждут ответа на первый.
   */
  private sessionReady: Promise<void> | null = null;

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    if (this.sessionReady) await this.sessionReady;
    else {
      let release!: () => void;
      this.sessionReady = new Promise<void>((resolve) => { release = resolve; });
      try { return await this.send<T>(method, path, body); } finally { release(); }
    }
    return this.send<T>(method, path, body);
  }

  private async send<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.base}${path}`;
    const init: RequestInit = {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(DEVICE_TZ ? { "X-Timezone": DEVICE_TZ } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    };
    const res = await this.fetchImpl(url, init);
    const text = await res.text();
    let payload: unknown = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = text;
      }
    }
    if (!res.ok) {
      const detail = payload && typeof payload === "object"
        ? payload as { message?: unknown } : null;
      const message = typeof detail?.message === "string" && detail.message.trim()
        ? detail.message : null;
      const msg = message ??
        (payload &&
          typeof payload === "object" &&
          "error" in payload &&
          typeof (payload as { error: unknown }).error === "string"
          ? (payload as { error: string }).error
          : null) ?? `HTTP ${res.status}`;
      throw new StorefrontApiError(msg, res.status, payload);
    }
    return payload as T;
  }

  // Menu drafts share the same first-cookie barrier as catalog/auth/cart reads.
  menuDraft = {
    read: () => this.request<{ draft: unknown }>("GET", "/v1/menu-draft"),
    save: (body: { expected_version: number; recipe_ids: string[] }) =>
      this.request<{ draft: unknown }>("PUT", "/v1/menu-draft", body),
  };

  // ─── Auth ────────────────────────────────────────────────────────────
  auth = {
    requestMagicLink: (email: string) =>
      this.request<AuthMagicLinkRequestResponse>(
        "POST",
        "/v1/auth/magic-link/request",
        { email },
      ),
    verifyToken: (token: string) =>
      this.request<AuthMagicLinkVerifyResponse>(
        "GET",
        `/v1/auth/magic-link/verify?token=${encodeURIComponent(token)}`,
      ),
    // §5.11 Ф4 — регистрация/вход по телефону (SMS-код).
    requestSmsCode: (input: { phone: string; captcha_token?: string }) =>
      this.request<AuthSmsRequestResponse>("POST", "/v1/auth/sms/request", input),
    verifySmsCode: (input: { phone: string; code: string; name?: string; birthday?: string; ref_code?: string }) =>
      this.request<AuthSmsVerifyResponse>("POST", "/v1/auth/sms/verify", input),
    logout: async () => {
      const result = await this.request<{ status: "ok" }>("POST", "/v1/auth/logout");
      // Logout removes the cookie. Bootstrap the next guest once again before
      // allowing parallel menu/catalog/cart requests to resolve their owner.
      this.sessionReady = null;
      return result;
    },
    me: () => this.request<AuthMeResponse>("GET", "/v1/auth/me"),
  };

  // ─── Catalog ─────────────────────────────────────────────────────────
  catalog = {
    categories: () =>
      this.request<{ categories: Category[] }>("GET", "/v1/catalog/categories"),
    productsByCategory: (categoryId: string) =>
      this.request<{ category: Category; products: Product[] }>(
        "GET",
        `/v1/catalog/categories/${encodeURIComponent(categoryId)}/products`,
      ),
    product: (productId: string) =>
      this.request<{ product: Product }>(
        "GET",
        `/v1/catalog/products/${encodeURIComponent(productId)}`,
      ),
  };

  // ─── Контент из CDP (Фаза 3) ─────────────────────────────────────────
  content = {
    // Промо-баннер главной. banner:null — активной акции-баннера в CDP нет (не показываем).
    promoBanner: () =>
      this.request<{ banner: PromoBanner | null }>("GET", "/v1/promo-banner"),
  };

  productNutrition = {
    query: (input: { basis: { kind: 'per100g' | 'per100ml' } | { kind: 'pack'; unit?: 'g' | 'ml' } | { kind: 'custom'; amount: number; unit: 'g' | 'ml' }; selection?: {category?:string;search?:string}; constraints?: import('./nutrition').NutrientConstraints; sort?: {nutrient:import('./nutrition').Nutrient;direction:'asc'|'desc'} | null; page:{size:number;offset:number}; snapshotVersion?:string|null }) => this.request<import('./types').ProductNutritionPage>('POST','/v1/product-nutrition/query', input),
  };

  storefrontContent = {
    shelves: () => this.request<{ shelves: import("./types").StorefrontShelf[] }>("GET", "/v1/storefront/shelves"),
    recipes: (params: { limit?: number; offset?: number; search?: string; category?: string; maxTime?: number; diet?: string; facets?: boolean } & import("./types").RecipeNutritionFilters = {}) => {
      const query = new URLSearchParams();
      if (params.limit !== undefined) query.set("limit", String(params.limit));
      if (params.offset !== undefined) query.set("offset", String(params.offset));
      if (params.search) query.set("search", params.search);
      if (params.category) query.set("category", params.category);
      if (params.maxTime !== undefined) query.set("maxTime", String(params.maxTime));
      if (params.diet) query.set("diet", params.diet);
      if (params.facets) query.set("facets", "1");
      for (const key of ['kcalMin','kcalMax','proteinMin','proteinMax','fatMin','fatMax','carbsMin','carbsMax','nutritionSort','catalogVersion'] as const) {
        if (params[key] !== undefined) query.set(key, String(params[key]));
      }
      const suffix = query.toString() ? `?${query.toString()}` : "";
      return this.request<import("./types").StorefrontRecipePage>("GET", `/v1/recipes${suffix}`);
    },
    compareRecipes: (ids: string[]) => this.request<{ recipes: import('./types').StorefrontRecipe[] }>('GET', `/v1/recipes/compare?ids=${ids.map(encodeURIComponent).join(',')}`),
    recipe: (id: string) => this.request<{ recipe: import("./types").StorefrontRecipe }>("GET", `/v1/recipes/${encodeURIComponent(id)}`),
  };

  // ─── Рекомендации (механизм рекомендаций CDP) ─────────────────────────
  // Кросс-селл карточки товара и блок «Рекомендуем вам» питает движок CDP (BFF ходит в
  // POST /v1/recommendations/preview). Профиль берётся из сессии на BFF. products:[] —
  // движок пуст/недоступен → фронт деградирует (кросс-селл на локальную выборку, блок скрыт).
  recommendations = {
    crossSell: (productId: string, categoryId?: string) => {
      const qs = new URLSearchParams({ product: productId });
      if (categoryId) qs.set("category", categoryId);
      return this.request<{ products: Product[] }>(
        "GET",
        `/v1/recommendations/cross-sell?${qs.toString()}`,
      );
    },
    forYou: () =>
      this.request<{ products: Product[] }>("GET", "/v1/recommendations/for-you"),
  };

  // ─── Персональное предложение (механизм NBO CDP) ──────────────────────
  // Лучшее предложение для клиента прямо сейчас на сайте вычисляет движок CDP (BFF ходит в
  // GET /v1/profiles/:id/best-offer под профилем из сессии). offer:null — предложения нет
  // (гость/«холодный» профиль/движок недоступен) → фронт прячет блок.
  nbo = {
    bestOffer: () =>
      this.request<{ offer: NboOffer | null }>("GET", "/v1/nbo/best-offer"),
  };

  // ─── Точки доставки (НСИ CDP) ─────────────────────────────────────────
  outlets = {
    // Список точек доставки для выбора в шапке. [] — справочник ещё не засеян / CDP недоступен.
    list: () => this.request<{ outlets: Outlet[] }>("GET", "/v1/outlets"),
  };

  // ─── Cart ────────────────────────────────────────────────────────────
  cart = {
    get: () => this.request<{ cart: Cart }>("GET", "/v1/cart"),
    addItem: (productId: string, qty = 1) =>
      this.request<{ cart: Cart; newQty: number }>(
        "POST",
        "/v1/cart/items",
        { productId, qty },
      ),
    setItemQty: (productId: string, qty: number) =>
      this.request<{ cart: Cart; newQty: number }>(
        "PATCH",
        `/v1/cart/items/${encodeURIComponent(productId)}`,
        { qty },
      ),
    removeItem: (productId: string) =>
      this.request<{ cart: Cart }>(
        "DELETE",
        `/v1/cart/items/${encodeURIComponent(productId)}`,
      ),
    clear: () =>
      this.request<{ cart: Cart; itemsCleared: number }>("DELETE", "/v1/cart"),
  };

  // ─── Orders ──────────────────────────────────────────────────────────
  orders = {
    start: () => this.request<{ cart: Cart }>("POST", "/v1/checkout/start"),
    // BUG-8 — validate a promo against the current cart (no consumption).
    // outletCode (опц.) — выбранная точка: квота уходит в чекаут той же точкой, что и заказ.
    previewPromo: (code: string, outletCode?: string | null) =>
      this.request<PromoPreview>("POST", "/v1/promo/preview", { code, outletCode: outletCode ?? null }),
    place: (input: PlaceOrderInput) =>
      this.request<{ order: Order }>("POST", "/v1/orders", input),
    list: () => this.request<{ orders: Order[] }>("GET", "/v1/orders"),
    get: (orderId: string) =>
      this.request<{ order: Order }>(
        "GET",
        `/v1/orders/${encodeURIComponent(orderId)}`,
      ),
    markDelivered: (orderId: string) =>
      this.request<{ order: Order }>(
        "POST",
        `/v1/orders/${encodeURIComponent(orderId)}/delivered`,
      ),
  };

  // ─── Profile / Loyalty ───────────────────────────────────────────────
  profile = {
    me: () => this.request<{ profile: Profile }>("GET", "/v1/profile/me"),
    addresses: () =>
      this.request<{ addresses: SavedAddress[] }>("GET", "/v1/profile/addresses"),
    // Дорожка Д, Д4: «Ограничения в питании» — тот же реестр и запись, что окно помощника.
    constraints: () => this.request<{ constraints: CustomerConstraints | null }>("GET", "/v1/profile/constraints"),
    saveConstraints: (body: { forbidden: string[]; requirements: string[]; expected_version: string; consent_text_version: string | null }) =>
      this.request<{ constraints: CustomerConstraints }>("PUT", "/v1/profile/constraints", { constraints: body }),
  };

  loyalty = {
    me: () => this.request<{ loyalty: LoyaltyView }>("GET", "/v1/loyalty/me"),
    transactions: () =>
      this.request<{ transactions: LoyaltyTransaction[] }>(
        "GET",
        "/v1/loyalty/transactions",
      ),
    promoCodes: () =>
      this.request<{ promoCodes: LoyaltyPromoCode[] }>(
        "GET",
        "/v1/loyalty/promo-codes",
      ),
    // Мульти-валюта (ТРЕК 4): все кошельки профиля + курсы обмена.
    wallets: () =>
      this.request<{ wallets: WalletView[]; exchangeRates: ExchangeRateView[] }>(
        "GET",
        "/v1/loyalty/wallets",
      ),
    exchange: (input: { fromCurrency: string; toCurrency: string; amount: number }) =>
      this.request<ExchangeResult>("POST", "/v1/loyalty/exchange", input),
    // §5.11 Ф4 — веб-рефералка: реф-код гостя (advocate) + суммы наград.
    referral: () =>
      this.request<{ code: string | null; reward_advocate: number; reward_referred: number }>(
        "GET",
        "/v1/loyalty/referral",
      ),
    // §5.11 Ф4 — Wallet-ссылки (Apple .pkpass + Google save).
    walletLinks: () =>
      this.request<{ enabled: boolean; apple_pass_url: string | null; google_url: string | null }>(
        "GET",
        "/v1/loyalty/wallet-links",
      ),
  };

  // ─── Games (геймификация) ────────────────────────────────────────────
  // Delivery + spin happen in the embeddable game-host against the CDP edge.
  // The storefront only supplies the host's context (write-key + profile ref).
  games = {
    hostContext: () =>
      this.request<GameHostContext>("GET", "/v1/games/host-context"),
  };

  // ─── CDP web SDK / опыты (Фаза 2) ────────────────────────────────────
  // Контекст браузерного веб-SDK: публичный write-key + ref профиля + стабильный
  // cookie_id. По нему трекер шлёт события в движок, а оверлей тянет опыты (edge).
  // 503 → веб-SDK не настроен (нет CDP_WRITE_KEY) — фича просто выключена.
  cdp = {
    webContext: () => this.request<CdpWebContext>("GET", "/v1/cdp/web-context"),
    experienceRecipientToken: () => this.request<ExperienceRecipientAuthorization>("POST", "/v1/experiences/recipient-token"),
  };

  // ─── «Помощник покупок» (виджет cdp-web-chat, ТЗ §7.6) ───────────────
  // 200 всегда (BFF сам деградирует на инфра-сбоях) — writeKey:null значит
  // виджет вовсе не настроен, chatInitToken:null при непустом writeKey значит
  // «монтируй виджет, но SDK инициализируется анонимно».
  chat = {
    initToken: () => this.request<ChatInitTokenContext>("GET", "/v1/chat/init-token"),
  };

  // ─── Rewards catalog (потратить баллы) ───────────────────────────────
  rewards = {
    list: () => this.request<{ rewards: RewardCatalogItem[] }>("GET", "/v1/rewards"),
    redeem: (id: string) =>
      this.request<RewardRedeemResult>("POST", `/v1/rewards/${encodeURIComponent(id)}/redeem`),
  };

  achievements = {
    me: () => this.request<AchievementsView>("GET", "/v1/achievements/me"),
  };

  // ─── Миссии (ТРЕК 9) — «Мои миссии» с прогрессом ─────────────────────
  missions = {
    me: () => this.request<{ missions: MissionView[] }>("GET", "/v1/missions"),
  };
}

/** One loyalty wallet (currency) the customer holds. */
export interface WalletView {
  currency: string;
  balance: number;
  name: string;
  icon: string | null;
  isDefault: boolean;
  exchangeable: boolean;
}

export interface ExchangeRateView {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  minFrom: number;
  maxFrom: number | null;
}

export interface ExchangeResult {
  fromCurrency: string;
  toCurrency: string;
  fromAmount: number;
  toAmount: number;
  fromBalance: number;
  toBalance: number;
}

/** A reward credited when a mission completes. */
export interface MissionRewardView {
  currency: string | null;
  amount: number | null;
  currencyName: string | null;
  currencyIcon: string | null;
  rewardSlug: string | null;
  badgeSlug: string | null;
}

export interface MissionProgressView {
  currentValue: number;
  status: "in_progress" | "completed" | "rewarded";
  pct: number;
  completedAt: string | null;
  rewardedAt: string | null;
}

/** A mission shown to the customer with their current progress. Identified by `slug`
 *  — the public contract (`missions[]` в /v1/miniapp/loyalty) не раскрывает внутренние id. */
export interface MissionView {
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  goalKind: "count" | "sum";
  goalTarget: number;
  periodKind: "lifetime" | "daily" | "weekly" | "monthly";
  repeatable: boolean;
  reward: MissionRewardView;
  progress: MissionProgressView | null;
}

/** A reward in the catalog (what the customer can spend points on). */
export interface RewardCatalogItem {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  kind: "promo_percent" | "promo_fixed" | "promo_gift" | "raffle_entry";
  costPoints: number;
  /** §2.3 — валюта списания за награду (points/coins/cashback). */
  currency: string;
}

export interface RewardRedeemResult {
  kind: string;
  name: string;
  costPoints: number;
  newBalance: number;
  promoCode: string | null;
  raffleName: string | null;
}

export interface SavedAddress {
  address: string;
  name: string;
  phone: string;
  deliveryTime: string;
  lastUsedAt: string;
}

/**
 * Context the storefront hands to the embeddable game-host so it can talk to the
 * CDP experiences edge: a public write-key + the resolved CDP profile ref. The
 * game types themselves live in `components/games/types` (transport-agnostic).
 */
export interface GameHostContext {
  writeKey: string;
  surface: string;
  profileRef: string;
  /** Exact HTTP game origin, if the pending game is an explicitly approved demo. */
  gameOrigin?: string | null;
}

/**
 * Контекст браузерного веб-SDK CDP (GET /api/v1/cdp/web-context): публичный
 * write-key (события + опыты), поверхность, ref профиля (`pid:<id>`) и стабильный
 * cookie_id (чтобы события веб-SDK падали на тот же профиль, что и адресные опыты).
 */
export interface CdpWebContext {
  writeKey: string;
  surface: string;
  profileRef: string;
  cookieId: string;
  placementCode: string;
  /** Explicitly granted preference topics; absent/empty must fail closed per experience marker. */
  consentTopics?: string[];
}

/**
 * Контекст «Помощника покупок» (GET /api/v1/chat/init-token): короткоживущий
 * chatInitToken + публичный writeKey инструмента-чата. Любое из полей может
 * быть null — фронт (stores/chat.ts) деградирует, а не падает.
 */
export interface ChatInitTokenContext {
  chatInitToken: string | null;
  expiresIn: number | null;
  writeKey: string | null;
  /** Experience панели чата — движок применяет её серверную тему/название ТОЛЬКО если она передана в SDK. */
  experienceId: string | null;
}

export interface ExperienceRecipientAuthorization {
  recipient_token: string;
  expires_in: number;
  placement_id: string;
  placement_code: string;
}

export interface AchievementItem {
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  reward_points: number;
  unlocked: boolean;
  unlocked_at: string | null;
  /** §2.5 — прогресс к закрытому count-достижению («3 из 5»). null для открытых/не-count. */
  progress?: { current: number; target: number; pct: number } | null;
}

export interface AchievementsView {
  achievements: AchievementItem[];
  unlocked_count: number;
  total: number;
  newly_unlocked: string[];
}

// Singleton convenience client for app code. Tests inject their own.
export const storefront = new StorefrontClient();
